import React from 'react'
import { useUIStore } from '@/stores/uiStore'
import { SignalType } from '@/types/devices'

interface CanvasToolbarProps {
  onAddDevice?: () => void
  onAutoMap?: () => void
  onAskAi?: () => void
  isAskAiPending?: boolean
  isAskAiAvailable?: boolean
  askAiDisabledReason?: string
  onAutoLayout?: () => void
  onManageDevices?: () => void
  onMidiPanel?: () => void
  isMidiPanelOpen?: boolean
  onSignalTypeFilter?: (type: SignalType) => void
  activeSignalTypes?: Set<SignalType>
  isReadOnly?: boolean
}

const signalTypes: SignalType[] = [
  'audio',
  'midi',
  'sync',
  'cv_gate',
  'adat',
  'word_clock',
  'usb',
  'ethernet',
]

export const CanvasToolbar: React.FC<CanvasToolbarProps> = ({
  onAddDevice,
  onAutoMap,
  onAskAi,
  isAskAiPending = false,
  isAskAiAvailable = true,
  askAiDisabledReason,
  onAutoLayout,
  onManageDevices,
  onMidiPanel,
  isMidiPanelOpen = false,
  activeSignalTypes = new Set(signalTypes),
  isReadOnly = false,
}) => {
  const readOnlyTitle = 'Viewer access — read-only'
  const viewMode = useUIStore(s => s.viewMode)
  const setViewMode = useUIStore(s => s.setViewMode)

  // Unified routing button: uses AI when available (paid), otherwise
  // falls back to the local rule-based auto-mapper.
  const handleSmartRoute = () => {
    if (isAskAiAvailable && onAskAi) {
      onAskAi()
    } else if (onAutoMap) {
      onAutoMap()
    }
  }
  const smartRouteLabel = isAskAiPending
    ? 'Thinking…'
    : isAskAiAvailable
      ? 'Smart Route'
      : 'Auto-Route'
  const smartRouteTitle = isReadOnly
    ? readOnlyTitle
    : isAskAiAvailable
      ? (askAiDisabledReason ?? 'AI-powered routing suggestions (Claude)')
      : 'Auto-map connections using the offline rule engine'

  return (
    <div className="bg-surface-overlay border-b border-brd-subtle shadow-low">
      <div className="px-2 sm:px-4 py-2 flex items-center gap-1.5 sm:gap-3 overflow-x-auto scrollbar-none">
        {/* Main Actions */}
        <div className="flex gap-1 sm:gap-2 flex-shrink-0">
          <button
            onClick={onAddDevice}
            disabled={isReadOnly}
            className="btn btn-sm bg-accent text-white hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            title={isReadOnly ? readOnlyTitle : 'Add a new device'}
          >
            <span>+</span> <span className="hidden sm:inline">Add </span>Device
          </button>

          {/* Unified route button — AI when available, auto-map fallback */}
          <button
            onClick={handleSmartRoute}
            disabled={isReadOnly || isAskAiPending}
            className={`btn btn-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-[color:var(--accent-ink)] ${
              isAskAiAvailable
                ? 'bg-gradient-to-r from-accent to-second hover:brightness-110'
                : 'bg-accent hover:bg-[color:var(--accent-hover)]'
            }`}
            title={smartRouteTitle}
          >
            {isAskAiAvailable && <span>✨</span>}
            <span className="hidden sm:inline">{smartRouteLabel}</span>
            <span className="sm:hidden">{isAskAiPending ? '…' : 'Route'}</span>
          </button>

          <button
            onClick={onAutoLayout}
            disabled={isReadOnly}
            className="btn btn-sm bg-success text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            title={isReadOnly ? readOnlyTitle : 'Auto-arrange devices'}
          >
            Layout
          </button>

          <button
            onClick={onManageDevices}
            disabled={isReadOnly}
            className="btn btn-sm bg-warning text-[color:var(--accent-ink)] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            title={isReadOnly ? readOnlyTitle : 'Manage devices'}
          >
            <span className="hidden sm:inline">Manage</span>
            <span className="sm:hidden">Mgr</span>
          </button>

          <button
            onClick={onMidiPanel}
            className={`btn btn-sm transition-colors duration-150 whitespace-nowrap text-[color:var(--accent-ink)] ${
              isMidiPanelOpen
                ? 'bg-[color:var(--second-dark)] ring-2 ring-[color:var(--second-soft)]'
                : 'bg-second hover:brightness-110'
            }`}
            title="Open MIDI control panel"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              className="flex-shrink-0"
            >
              <circle
                cx="8"
                cy="8"
                r="7"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <circle cx="5" cy="6" r="1" fill="currentColor" />
              <circle cx="11" cy="6" r="1" fill="currentColor" />
              <circle cx="5" cy="10" r="1" fill="currentColor" />
              <circle cx="11" cy="10" r="1" fill="currentColor" />
              <circle cx="8" cy="8" r="1" fill="currentColor" />
            </svg>
            <span className="hidden sm:inline">MIDI</span>
          </button>
        </div>

        <div className="w-px h-6 bg-brd-subtle flex-shrink-0 hidden sm:block" />

        {/* View Mode */}
        <div className="flex gap-0.5 bg-surface-sunken rounded-lg p-0.5 flex-shrink-0">
          {(['graph', 'matrix', 'list'] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors duration-150 ${
                viewMode === mode
                  ? 'bg-surface-raised text-content-primary shadow-low'
                  : 'text-content-secondary hover:text-content-primary'
              }`}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </button>
          ))}
        </div>

        <div className="w-px h-6 bg-brd-subtle flex-shrink-0 hidden sm:block" />

        {/* Signal Type Filters — hidden on mobile */}
        <div className="hidden sm:flex gap-1 items-center flex-shrink-0">
          <span className="text-xs text-content-tertiary mr-1">Signals:</span>
          <div className="flex gap-1 flex-wrap">
            {signalTypes.slice(0, 4).map(type => (
              <div
                key={type}
                className={`w-2 h-2 rounded-full transition-opacity duration-150 ${
                  activeSignalTypes.has(type) ? 'opacity-100' : 'opacity-30'
                }`}
                style={{
                  backgroundColor:
                    type === 'audio'
                      ? '#FF6B6B'
                      : type === 'midi'
                        ? '#4ECDC4'
                        : type === 'sync'
                          ? '#45B7D1'
                          : '#FFA07A',
                }}
                title={type}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
